import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "@/components/site/Reveal";
import { company, serviceOptions } from "@/data/content";

const initial = {
  name: "",
  phone: "",
  email: "",
  location: "",
  service: "",
  description: "",
  timeline: "",
};

export const ContactForm = () => {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [submitError, setSubmitError] = useState("");

  const API = process.env.REACT_APP_BACKEND_URL;

  const set = (key) => (e) =>
    setValues((v) => ({ ...v, [key]: typeof e === "string" ? e : e.target.value }));

  const validate = () => {
    const err = {};
    if (!values.name.trim()) err.name = "Zadejte prosím jméno.";
    if (!values.phone.trim()) err.phone = "Zadejte prosím telefon.";
    else if (!/^[+\d][\d\s]{8,}$/.test(values.phone.trim()))
      err.phone = "Zadejte prosím platné telefonní číslo.";
    if (!values.email.trim()) err.email = "Zadejte prosím e-mail.";
    else if (!/^\S+@\S+\.\S{2,}$/.test(values.email.trim()))
      err.email = "Zadejte prosím platný e-mail.";
    if (!values.service) err.service = "Vyberte prosím typ služby.";
    if (!values.description.trim()) err.description = "Popište prosím stručně zakázku.";
    return err;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length) return;
    setStatus("loading");

    const form = new FormData();
    form.append("name", values.name.trim());
    form.append("phone", values.phone.trim());
    form.append("email", values.email.trim());
    form.append("location", values.location.trim());
    form.append("service", values.service);
    form.append("description", values.description.trim());
    form.append("timeline", values.timeline.trim());

    try {
      const res = await fetch(`${API}/api/inquiries`, {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Odeslání se nezdařilo.");
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setSubmitError(
        err.message ||
          "Poptávku se nepodařilo odeslat. Zkuste to prosím znovu nebo nám zavolejte."
      );
    }
  };

  const fieldClass = (key) =>
    `h-12 border-input bg-background focus-visible:ring-ring ${
      errors[key] ? "border-destructive" : ""
    }`;

  return (
    <section id="kontakt" className="scroll-mt-24 bg-background" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-accent">
              Nezávazná poptávka
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Řekněte nám stručně, co plánujete
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Stačí pár vět. Ozveme se Vám a domluvíme
              další postup — prohlídku, zaměření nebo rovnou nabídku.
            </p>
            <div className="mt-8 flex flex-col gap-2 border-t border-border pt-6 text-sm">
              <a href={company.phoneHref} className="font-semibold text-foreground transition-colors hover:text-accent">
                {company.phone}
              </a>
              <a href={company.emailHref} className="font-semibold text-foreground transition-colors hover:text-accent">
                {company.email}
              </a>
              <p className="mt-1 text-muted-foreground">
                {company.owner} · {company.ownerRole}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-8">
            {status === "success" ? (
              <div
                data-testid="form-success"
                className="flex h-full min-h-[380px] flex-col items-center justify-center rounded-md border border-border bg-card p-10 text-center"
              >
                <CheckCircle2 className="h-10 w-10 text-accent" />
                <h3 className="mt-5 text-2xl font-semibold text-foreground">
                  Děkujeme.
                </h3>
                <p className="mt-2 max-w-sm text-base text-muted-foreground">
                  Poptávku jsme přijali a ozveme se kvůli dalšímu postupu.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="rounded-md border border-border bg-card p-6 sm:p-10" data-testid="contact-form">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="name">Jméno *</Label>
                    <Input id="name" data-testid="input-name" value={values.name} onChange={set("name")} placeholder="Jan Novák" className={fieldClass("name")} />
                    {errors.name && <p className="text-xs text-destructive" data-testid="error-name">{errors.name}</p>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input id="phone" data-testid="input-phone" type="tel" value={values.phone} onChange={set("phone")} placeholder="+420" className={fieldClass("phone")} />
                    {errors.phone && <p className="text-xs text-destructive" data-testid="error-phone">{errors.phone}</p>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="email">E-mail *</Label>
                    <Input id="email" data-testid="input-email" type="email" value={values.email} onChange={set("email")} placeholder="vas@email.cz" className={fieldClass("email")} />
                    {errors.email && <p className="text-xs text-destructive" data-testid="error-email">{errors.email}</p>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="location">Lokalita</Label>
                    <Input id="location" data-testid="input-location" value={values.location} onChange={set("location")} placeholder="Plzeň a okolí" className={fieldClass("location")} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label>Typ služby *</Label>
                    <Select value={values.service} onValueChange={set("service")}>
                      <SelectTrigger data-testid="select-service" className={fieldClass("service")}>
                        <SelectValue placeholder="Vyberte službu" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover">
                        {serviceOptions.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.service && <p className="text-xs text-destructive" data-testid="error-service">{errors.service}</p>}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="timeline">Předpokládaný termín</Label>
                    <Input id="timeline" data-testid="input-timeline" value={values.timeline} onChange={set("timeline")} placeholder="např. jaro 2026" className={fieldClass("timeline")} />
                  </div>
                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <Label htmlFor="description">Stručný popis zakázky *</Label>
                    <Textarea
                      id="description"
                      data-testid="input-description"
                      value={values.description}
                      onChange={set("description")}
                      placeholder="Např. zateplení rodinného domu, cca 140 m² fasády…"
                      className={`min-h-[120px] border-input bg-background ${errors.description ? "border-destructive" : ""}`}
                    />
                    {errors.description && <p className="text-xs text-destructive" data-testid="error-description">{errors.description}</p>}
                  </div>
                </div>

                <div className="mt-8 flex flex-col items-start gap-4">
                  {status === "error" && submitError && (
                    <p
                      data-testid="form-error"
                      className="w-full rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                    >
                      {submitError}
                    </p>
                  )}
                  <Button
                    type="submit"
                    data-testid="form-submit"
                    disabled={status === "loading"}
                    className="h-12 w-full bg-primary px-8 text-primary-foreground hover:bg-primary/90 sm:w-auto"
                  >
                    {status === "loading" ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Odesíláme…
                      </>
                    ) : (
                      "Odeslat nezávaznou poptávku"
                    )}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Odesláním souhlasíte se zpracováním údajů pro účely vyřízení poptávky.
                  </p>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
